// frontend/src/components/homepage/HowMobileStack.js
//
// "How it works" as a still: the three steps as a plain vertical stack, each
// with a small self-contained still of what that stage looks like - a feed
// excerpt, one scored card, the phone with the push. Nothing here moves: no
// frame task, no scroll listener, no timer.
//
// Since owner round 2 (2026-09-04) this is ONLY the motion-off picture on a
// screen under 1000px and the prerender snapshot (puppeteer renders at 800px,
// and every word of the section's copy is in this markup as real text). A
// phone with motion on gets the pinned story at a compact scale instead - see
// HowItWorksLive.js.

import React from "react";
import HowSteps from "./HowSteps";
import { FeedPost, HowPhone } from "./HowStream";
import { LEADS, NOISE } from "./heroData";
import { STAGE_LABELS } from "../../homepage/howFeed";

/**
 * The posts the feed excerpt shows: two leads around one noise post.
 *
 * Built from whichever deck the section was given - the homepage's by default,
 * so the still is unchanged there. The noise post stays `NOISE[0]`: it is
 * profession-free chatter and reads correctly under any hero. The indices wrap,
 * so a deck shorter than three still yields three distinct-looking rows rather
 * than an `undefined` post.
 */
function buildExcerpt(leads) {
  const at = (i) => leads[i % leads.length];
  return [
    { post: at(1), isLead: true },
    { post: NOISE[0], isLead: false },
    { post: at(2), isLead: true },
  ];
}

/** The lead the scored card and the push both show. */
const scoredOf = (leads) => leads[1 % leads.length];

/** A stock clock for the still: no timer, and identical on every render. */
const STILL_CLOCK = { time: "09:41", date: "יום שלישי, 2 בספטמבר" };

/** The stage label strip the desktop stream carries, reused on each still. */
const StageLabel = ({ stage }) => (
  <div className="hl-how-lbl">
    <i />
    <span>{STAGE_LABELS[stage]}</span>
  </div>
);

/**
 * @param {object} props
 * @param {object} props.t the language's HOW_COPY entry.
 * @param {object[]} [props.leads] the deck the stills draw from; defaults to
 *   the homepage's own.
 */
export default function HowMobileStack({ t, leads = LEADS }) {
  const excerpt = buildExcerpt(leads);
  const scored = scoredOf(leads);
  return (
    <div className="hl-how-mobile" data-testid="hl-how-mobile">
      <div className="hl-how-head">
        <div className="hl-eyebrow">{t.eyebrow}</div>
        <h2 className="hl-h2 hl-how-en-h2" dir="ltr" lang="en">
          {t.h2}
        </h2>
        <p className="hl-sub">{t.sub}</p>
      </div>

      {/* Every step lit: this is the whole story at once, not a scroll through it. */}
      <HowSteps t={t} stage={3} railFull />

      <div className="hl-how-stills">
        <div className="hl-how-still hl-s1">
          <StageLabel stage={1} />
          {/* Decorative: an un-scanned feed carries its stage labels and score
              lines at opacity 0 (the desktop stream lights them as it scans),
              and a crawler or a screen reader must not read hidden text as
              content. The step copy in HowSteps is the real text. */}
          <div className="hl-how-still-feed" aria-hidden="true">
            {excerpt.map(({ post, isLead }) => (
              <FeedPost key={post.grp} post={post} isLead={isLead} t={t} />
            ))}
          </div>
        </div>

        <div className="hl-how-still hl-s2">
          <StageLabel stage={2} />
          <div className="hl-how-still-feed">
            <div className="hl-how-scan" />
            <FeedPost post={scored} isLead t={t} className="hl-how-post hl-scanned hl-hot" />
          </div>
        </div>

        <div className="hl-how-still hl-s3">
          <StageLabel stage={3} />
          <div className="hl-how-still-phone">
            <HowPhone t={t} text={t.pushPrefix + scored.txt} clock={STILL_CLOCK} />
          </div>
        </div>
      </div>
    </div>
  );
}
